
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import ToastContainer from '../components/Toast';

// ─── TYPES ────────────────────────────────────────────────────────────────────

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (message: string, action?: Toast['action']) => void;
  error: (message: string, action?: Toast['action']) => void;
  info: (message: string, action?: Toast['action']) => void;
  warning: (message: string, action?: Toast['action']) => void;
}

// ─── CONTEXT ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 3;

let toastCounter = 0;

// ─── PROVIDER ─────────────────────────────────────────────────────────────────

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${++toastCounter}-${Date.now()}`;
    const duration = toast.duration ?? (toast.type === 'error' ? 6000 : 4000);

    setToasts(prev => {
      const next = [...prev, { ...toast, id, duration }];
      // If exceeding max, remove the oldest
      if (next.length > MAX_VISIBLE) {
        const removed = next.shift();
        if (removed) {
          const timer = timersRef.current.get(removed.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(removed.id);
          }
        }
      }
      return next;
    });

    // Auto-dismiss timer
    const timer = setTimeout(() => {
      removeToast(id);
    }, duration);
    timersRef.current.set(id, timer);
  }, [removeToast]);

  const success = useCallback((message: string, action?: Toast['action']) => {
    addToast({ type: 'success', message, action });
  }, [addToast]);

  const error = useCallback((message: string, action?: Toast['action']) => {
    addToast({ type: 'error', message, action });
  }, [addToast]);

  const info = useCallback((message: string, action?: Toast['action']) => {
    addToast({ type: 'info', message, action });
  }, [addToast]);

  const warning = useCallback((message: string, action?: Toast['action']) => {
    addToast({ type: 'warning', message, action });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, info, warning }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
};

// ─── HOOK ─────────────────────────────────────────────────────────────────────

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// ─── STANDALONE TOAST (for use outside React tree, e.g. exportService) ────────

let _globalToast: ToastContextValue | null = null;

export const setGlobalToast = (toast: ToastContextValue) => {
  _globalToast = toast;
};

export const getGlobalToast = (): ToastContextValue | null => _globalToast;
